import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { collection, doc, getDocs, getFirestore, query, setDoc, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

import { User } from '../types';
import useAuth from '../hooks/useAuth';

interface UsersResponse {
  users: User[];
  nextPageToken: string | null;
}

const availablePermissions = [
  'conduct_polls',
  'manage_polls',
  'validate_polls',
  'view_dashboard',
  'view_throwing',
  'manage_users',
  'view_voters',
  'manage_voters',
  'manage_organizations',
  'view_voter_groups',
  'manage_voter_groups',
  'delete_voter_groups',
  'view_precincts',
  'view_barangays',
  'view_responses',
  'view_reports',
  'tv_monitor',
  'scan_qr_stub_release',
  'scan_qr_final_release',
  'scan_qr_exit_poll',
  'view_exit_polls',
  'admin'
] as const;

type PermissionKey = typeof availablePermissions[number];

const UserManagement: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [updatingPermissions, setUpdatingPermissions] = useState<{ [uid: string]: boolean }>({});
  
  const observer = useRef<IntersectionObserver | null>(null);
  const lastUserElementRef = useCallback((node: HTMLTableRowElement | null) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && nextPageToken) {
        handleListUsers();
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, nextPageToken]);

  const loadUserPermissions = async (user: User) => {
    let permissions = {
      manage_polls: false,
      conduct_polls: false,
      validate_polls: false,
      view_dashboard: false,
      view_throwing: false,
      manage_users: false,
      view_voters: false,
      manage_voters: false,
      manage_organizations: false,
      view_voter_groups: false,
      manage_voter_groups: false,
      delete_voter_groups: false,
      view_precincts: false,
      view_barangays: false,
      view_responses: false,
      view_reports: false,
      tv_monitor: false,
      admin: false,
      scan_qr_stub_release: false,
      scan_qr_final_release: false,
      scan_qr_exit_poll: false,
      view_exit_polls: false,
      analytics: false
    };

    try {
      const db = getFirestore();
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', user.email));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        const data = userDoc.data();
        if (data.permissions) {
          permissions = {
            manage_polls: data.permissions.manage_polls ?? false,
            conduct_polls: data.permissions.conduct_polls ?? false,
            validate_polls: data.permissions.validate_polls ?? false,
            view_dashboard: data.permissions.view_dashboard ?? false,
            view_throwing: data.permissions.view_throwing ?? false,
            manage_users: data.permissions.manage_users ?? false,
            view_voters: data.permissions.view_voters ?? false,
            manage_voters: data.permissions.manage_voters ?? false,
            manage_organizations: data.permissions.manage_organizations ?? false,
            view_voter_groups: data.permissions.view_voter_groups ?? false,
            manage_voter_groups: data.permissions.manage_voter_groups ?? false,
            delete_voter_groups: data.permissions.delete_voter_groups ?? false,
            view_precincts: data.permissions.view_precincts ?? false,
            view_barangays: data.permissions.view_barangays ?? false,
            view_responses: data.permissions.view_responses ?? false,
            view_reports: data.permissions.view_reports ?? false,
            tv_monitor: data.permissions.tv_monitor ?? false,
            admin: data.permissions.admin ?? false,
            scan_qr_stub_release: data.permissions.scan_qr_stub_release ?? false,
            scan_qr_final_release: data.permissions.scan_qr_final_release ?? false,
            scan_qr_exit_poll: data.permissions.scan_qr_exit_poll ?? false,
            view_exit_polls: data.permissions.view_exit_polls ?? false,
            analytics: data.permissions.analytics ?? false
          };
        }
      }
    } catch (error) {
      console.error(`Error loading permissions for user ${user.email}:`, error);
    }
    
    return {
      ...user,
      permissions
    };
  };

  const handleListUsers = async () => {
    if (loading || (!nextPageToken && users.length > 0)) return;
    setLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const listUsers = httpsCallable<{ pageToken?: string }, UsersResponse>(functions, 'list_users');
      
      const result = await listUsers(nextPageToken ? { pageToken: nextPageToken } : {});
      const data = result.data;
      
      if (data && Array.isArray(data.users)) {
        const usersWithPermissions = await Promise.all(data.users.map(loadUserPermissions));
        setUsers(prevUsers => {
          const newUsers = [...prevUsers, ...usersWithPermissions];
          // Remove duplicates based on id
          return Array.from(new Map(newUsers.map(user => [user.uid, user])).values());
        });
        setNextPageToken(data.nextPageToken);
        console.log(`Added ${usersWithPermissions.length} users. Total: ${users.length + usersWithPermissions.length}`);
      } else {
        console.error('Unexpected response format:', data);
        throw new Error('Unexpected response format');
      }
    } catch (error: any) {
      console.error('Error fetching users:', error);
      setError(`Failed to fetch users: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = async (uid: string, permission: PermissionKey, isChecked: boolean) => {
    setUpdatingPermissions(prev => ({ ...prev, [uid]: true }));
    try {
      const user = users.find(u => u.uid === uid);
      if (!user) throw new Error('User not found');

      const newPermissions = {
        ...user.permissions,
        [permission]: isChecked
      };

      const db = getFirestore();
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', user.email));
      const querySnapshot = await getDocs(q);

      let userDocRef;
      if (!querySnapshot.empty) {
        // If document exists, use its reference
        userDocRef = querySnapshot.docs[0].ref;
      } else {
        // If no document found, create a new one using the uid as document ID
        userDocRef = doc(db, 'users', uid);
      }

      // Set or update the document
      await setDoc(userDocRef, 
        {
          uid: uid,
          email: user.email,
          display_name: user.display_name,
          permissions: newPermissions
        },
        { merge: true }
      );

      // Update local state
      setUsers(users.map(u => 
        u.uid === uid ? { ...u, permissions: newPermissions } : u
      ));
    } catch (error: any) {
      console.error('Error updating user permissions:', error);
      setError(`Failed to update permissions: ${error.message || 'Unknown error'}`);
    } finally {
      setUpdatingPermissions(prev => ({ ...prev, [uid]: false }));
    }
  };

  useEffect(() => {
    if (users.length === 0 && user?.permissions.manage_users) {
      handleListUsers();
    }
  }, [user]);

  if (!user?.permissions.manage_users && !authLoading) {
    return (
      <Typography variant="h6" align="center">
        You do not have permission to access this page.
      </Typography>
    );
  }

  if (loading || authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, margin: 'auto' }}>
      <Typography variant="h4">User Management</Typography>
      {error && (
        <Typography color="error" align="center">
          {error}
        </Typography>
      )}
      {users.length > 0 && (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="user management table">
            <TableHead>
              <TableRow>
                <TableCell>Display Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Permissions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user, index) => (
                <TableRow key={user.uid} ref={index === users.length - 1 ? lastUserElementRef : null}>
                  <TableCell>{user.display_name ?? 'N/A'}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <FormGroup>
                      {availablePermissions.map((permission) => (
                        <FormControlLabel
                          key={permission}
                          control={
                            <Checkbox
                              checked={user.permissions[permission as keyof User['permissions']] || false}
                              onChange={(event) => handlePermissionChange(user.uid, permission, event.target.checked)}
                              disabled={updatingPermissions[user.uid]}
                            />
                          }
                          label={permission}
                        />
                      ))}
                    </FormGroup>
                    {updatingPermissions[user.uid] && <CircularProgress size={24} />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {!loading && !authLoading && users.length === 0 && !error && (
        <Typography align="center">No users to display.</Typography>
      )}
    </Box>
  );
};

export default UserManagement;
